import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { MapPin, ArrowRight, Menu, X } from "lucide-react";
import { MeetTheTeamLink } from "@/components/MeetTheTeam";

const NAV_LINKS = [
  { to: "/home-services", label: "Home Services", match: "/home-services" },
  { to: "/tourism", label: "Tourism", match: "/tourism" },
  { to: "/tourism/case-studies", label: "Case Studies" },
  { to: "/platforms", label: "Custom Maps" },
  { to: "/sample-ads", label: "Sample Ads", match: "/sample-ads" },
];

const SiteShell = ({ children }) => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll while menu open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const isActive = (match) =>
    match && location.pathname.startsWith(match);

  return (
    <div className="min-h-screen flex flex-col bg-[var(--af-navy)] text-[var(--af-cream)]">
      {/* Header */}
      <header
        className="w-full bg-[var(--af-navy)]/95 backdrop-blur-md sticky top-0 z-50 border-b border-[var(--af-line)]/60"
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3.5 sm:py-5 flex items-center justify-between gap-3">
          <Link
            to="/"
            className="flex items-center gap-2 group min-w-0"
            data-testid="header-logo-link"
          >
            <MapPin
              size={18}
              className="text-[var(--af-cream)] -mt-0.5 flex-shrink-0"
              strokeWidth={1.5}
            />
            <span className="font-display tracking-wide text-sm sm:text-lg whitespace-nowrap">
              <span className="font-semibold">ANNE FLINN</span>{" "}
              <span className="text-[var(--af-cream)]/75 font-normal hidden sm:inline">
                ADVERTISING
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7 text-sm">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`hover:text-[var(--af-lime)] transition-colors ${
                  isActive(l.match)
                    ? "text-[var(--af-lime)]"
                    : "text-[var(--af-cream)]"
                }`}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/#packages"
              className="text-[var(--af-cream)] hover:text-[var(--af-lime)] transition-colors"
              data-testid="nav-packages-link"
            >
              Packages
            </Link>
            <Link
              to="/#lead-form"
              className="btn-lime"
              data-testid="nav-get-started-btn"
            >
              Let&apos;s Talk <ArrowRight size={14} />
            </Link>
          </nav>

          {/* Mobile: hamburger button — high-contrast lime so it pops on dark navy */}
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="lg:hidden inline-flex items-center gap-2 px-3 h-11 rounded-md bg-[var(--af-lime)] text-[var(--af-navy)] font-semibold text-xs uppercase tracking-[0.14em] hover:opacity-90 transition-opacity shadow-md"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            data-testid="nav-mobile-menu-toggle"
          >
            {menuOpen ? <X size={18} strokeWidth={2.4} /> : <Menu size={18} strokeWidth={2.4} />}
            <span>{menuOpen ? "Close" : "Menu"}</span>
          </button>
        </div>
      </header>

      {/* Mobile menu panel — rendered outside <header> because the header's
          backdrop-blur creates a containing block that would trap position:fixed
          descendants. Sibling placement lets it truly cover the viewport. */}
      {menuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[45] bg-[var(--af-navy)] overflow-y-auto pt-20 sm:pt-24"
          data-testid="nav-mobile-menu-panel"
        >
          <nav className="px-5 py-6 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`text-2xl font-display py-4 border-b border-[var(--af-line)] transition-colors ${
                  isActive(l.match)
                    ? "text-[var(--af-lime)]"
                    : "text-[var(--af-cream)] hover:text-[var(--af-lime)]"
                }`}
                data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/#packages"
              onClick={() => setMenuOpen(false)}
              className="text-2xl font-display py-4 border-b border-[var(--af-line)] text-[var(--af-cream)] hover:text-[var(--af-lime)] transition-colors"
              data-testid="nav-mobile-packages-link"
            >
              Packages
            </Link>
            <Link
              to="/#lead-form"
              onClick={() => setMenuOpen(false)}
              className="btn-lime mt-6 self-start"
              data-testid="nav-mobile-cta-btn"
            >
              Let&apos;s Talk <ArrowRight size={14} />
            </Link>

            <div className="mt-10 text-xs text-[var(--af-cream)]/55 leading-relaxed">
              Anne Flinn Advertising · Charlottesville, Virginia
            </div>
          </nav>
        </div>
      )}

      <main className="flex-1">{children}</main>

      {/* Marquee strip */}
      <div className="bg-[var(--af-navy)] border-y border-[var(--af-line)] overflow-hidden">
        <div className="marquee-track flex gap-12 py-4 whitespace-nowrap text-sm text-[var(--af-cream)]/85">
          {[
            "90-Day Impact Campaigns",
            "Boutique Agency, Big-City Capabilities",
            "Woman-Owned, Charlottesville-Local",
            "Multi-State Campaign Specialists",
            "45,000+ Visits Driven for Nelson County",
            "Expert in Tourism + Home Services",
          ]
            .concat([
              "90-Day Impact Campaigns",
              "Boutique Agency, Big-City Capabilities",
              "Woman-Owned, Charlottesville-Local",
              "Multi-State Campaign Specialists",
              "45,000+ Visits Driven for Nelson County",
              "Expert in Tourism + Home Services",
            ])
            .map((t, i) => (
              <span key={i} className="flex items-center gap-3">
                {t}
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)] inline-block" />
              </span>
            ))}
        </div>
      </div>

      {/* Footer */}
      <footer
        className="bg-[var(--af-navy)] text-[var(--af-cream)]"
        data-testid="site-footer"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid md:grid-cols-3 gap-10 border-t border-[var(--af-line)]">
          <div>
            <Link to="/" className="flex items-center gap-2 mb-3">
              <MapPin size={18} strokeWidth={1.5} />
              <span className="font-display tracking-wide">
                <span className="font-semibold">ANNE FLINN</span>{" "}
                <span className="text-[var(--af-cream)]/75 font-normal">
                  ADVERTISING
                </span>
              </span>
            </Link>
            <p className="text-sm opacity-75 max-w-xs leading-relaxed">
              A boutique advertising agency in Charlottesville, Virginia.
              Helping home services pros and tourism brands own their markets.
            </p>
            <div
              className="mt-5 inline-flex items-center gap-3 px-3.5 py-2 rounded-full border border-[var(--af-line)] bg-[var(--af-navy-soft)]/60"
              data-testid="footer-vifl-badge"
            >
              <img
                src="/media/clients/vifl-horizontal.png"
                alt="Virginia Is For Lovers"
                className="h-4 w-auto opacity-95 flex-shrink-0"
              />
              <div className="text-[10px] uppercase tracking-widest text-[var(--af-cream)]/75 font-medium">
                VTC grant-aligned partner
              </div>
            </div>
          </div>
          <div>
            <div className="font-eyebrow text-xs uppercase opacity-60 mb-3">
              Industries We Serve
            </div>
            <ul className="space-y-1.5 text-sm">
              <li>
                <Link
                  to="/home-services"
                  className="hover:text-[var(--af-lime)]"
                >
                  Home Services →
                </Link>
              </li>
              <li>
                <Link to="/tourism" className="hover:text-[var(--af-lime)]">
                  Tourism →
                </Link>
              </li>
              <li>
                <Link to="/sample-ads" className="hover:text-[var(--af-lime)]">
                  Sample Ads →
                </Link>
              </li>
              <li>
                <Link to="/platforms" className="hover:text-[var(--af-lime)]">
                  Custom Maps →
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <div className="font-eyebrow text-xs uppercase opacity-60 mb-3">
              Ready to Talk?
            </div>
            <Link
              to="/#lead-form"
              className="btn-lime"
              data-testid="footer-cta-btn"
            >
              Free Market Audit <ArrowRight size={14} />
            </Link>
            <div className="mt-3">
              <MeetTheTeamLink />
            </div>
            <div className="text-xs opacity-60 mt-4">
              Charlottesville, Virginia
            </div>
          </div>
        </div>
        <div className="dashed-divider">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 text-xs opacity-60 flex flex-col sm:flex-row gap-2 justify-between">
            <div>© {new Date().getFullYear()} Anne Flinn Advertising</div>
            <div>Advertise Smarter with Us.</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SiteShell;
